// Team › Alerts (`/c/{slug}/agents/alerts`; MONITORING-P1, docs/design/2026-11-01-monitoring-in-
// companyos.md §3): what the monitoring raised about the company's DEs — stuck runs, post storms,
// stalled job stages, tripped guards — newest first. Active (open and acked) by default; Resolved
// and All, and one kind, on the filter bar. Each row names its DE and links its run; Ack says
// someone is on it, Resolve closes it. Managers only (the API's rule; the route gates it too).
// Calls on load: `monitoring.alerts.list` (the filter as `?state`, `?kind`), plus the cached DE
// list. MONITORING-P2: the header links Team › Fleet and Team › Budget incidents.
import {
  Badge,
  Button,
  EmptyState,
  InfoTip,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { SirenIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { type AlertFilter, invalidateMonitoring, monitorAlertsQuery } from "./agent-queries";
import { LIST_BODY } from "./agents-ux";
import { ALERT_KIND_LABELS, ALERT_STATE_LABELS, type MonitorAlert } from "./run-telemetry";
import { useDeNames } from "./runs-screen";

const STATES = [
  ["active", "Active"],
  ["resolved", "Resolved"],
  ["all", "All"],
] as const;

const KINDS = Object.keys(ALERT_KIND_LABELS) as MonitorAlert["kind"][];

const STATE_TONE: Record<MonitorAlert["state"], "danger" | "info" | "neutral"> = {
  open: "danger",
  acked: "info",
  resolved: "neutral",
};

export function AlertsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const [filter, setFilter] = useState<AlertFilter>({ state: "active" });
  const q = useQuery(monitorAlertsQuery(api, company.id, filter));
  const names = useDeNames(api, company.id);
  const kindId = useId();
  const rows = q.data?.data ?? [];
  return (
    <div className={LIST_BODY} data-slot="alerts">
      <LocationHeader
        icon={SirenIcon}
        title="Alerts"
        badge={
          <InfoTip label="About alerts">
            Raised from what your DEs' hosts report: a run with no news for too long, too many posts
            in a short time, a job stage past its time budget, or a guard rule tripped. An alert
            clears by itself when the cause goes away, or when you resolve it.
          </InfoTip>
        }
        actions={
          <span className="flex gap-3 text-sm">
            <Link
              to="/c/$slug/agents/fleet"
              params={{ slug: company.slug }}
              search={keepFocus}
              className="text-primary-text underline-offset-4 hover:underline"
              data-slot="alerts-fleet-link"
            >
              Fleet
            </Link>
            <Link
              to="/c/$slug/agents/budget-incidents"
              params={{ slug: company.slug }}
              search={keepFocus}
              className="text-primary-text underline-offset-4 hover:underline"
              data-slot="alerts-incidents-link"
            >
              Budget incidents
            </Link>
          </span>
        }
      />
      <Toolbar bleed aria-label="Alert filters">
        <ToggleGroup
          variant="chip"
          value={[filter.state]}
          onValueChange={(v: unknown[]) => {
            const next = v[0] as AlertFilter["state"] | undefined;
            if (next) setFilter((f) => ({ ...f, state: next }));
          }}
          aria-label="Show"
        >
          {STATES.map(([value, label]) => (
            <ToggleGroupItem key={value} value={value}>
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <label htmlFor={kindId} className="sr-only">
          Kind
        </label>
        <NativeSelect
          id={kindId}
          size="sm"
          value={filter.kind ?? ""}
          onChange={(e) =>
            setFilter((f) => ({
              ...f,
              kind: (e.target.value || undefined) as MonitorAlert["kind"] | undefined,
            }))
          }
        >
          <NativeSelectOption value="">Every kind</NativeSelectOption>
          {KINDS.map((k) => (
            <NativeSelectOption key={k} value={k}>
              {ALERT_KIND_LABELS[k]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Toolbar>
      <div className="flex min-w-0 flex-col px-6 pt-3 max-md:px-4">
        {q.isPending ? (
          <Loading label="Loading alerts" rows={4} />
        ) : q.isError ? (
          <LoadError what="Alerts" error={q.error} />
        ) : !rows.length ? (
          <EmptyState
            variant="card"
            icon={SirenIcon}
            title={filter.state === "active" ? "No open alerts" : "No alerts"}
            description="Alerts show up here when a DE's host reports something off."
          />
        ) : (
          <Table data-slot="alerts-table">
            <TableCaption>Monitoring alerts, newest first.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Alert</TableHead>
                <TableHead scope="col">Digital employee</TableHead>
                <TableHead scope="col">Run</TableHead>
                <TableHead scope="col">Opened</TableHead>
                <TableHead scope="col">Last seen</TableHead>
                <TableHead scope="col">State</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => (
                <AlertRow key={a.id} api={api} company={company} alert={a} names={names} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function AlertRow({
  api,
  company,
  alert: a,
  names,
}: {
  api: Api;
  company: CompanyAccess;
  alert: MonitorAlert;
  names: Map<string, string>;
}) {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (state: "acked" | "resolved") =>
      api.client.monitoring.alerts.update({
        params: { co: company.id, id: a.id },
        body: { state },
      }),
    onSettled: () => invalidateMonitoring(qc, company.id),
  });
  const kind = ALERT_KIND_LABELS[a.kind];
  return (
    <TableRow data-alert={a.id} data-kind={a.kind} data-state={a.state}>
      <TableHead scope="row" className="font-medium whitespace-normal">
        <span className="flex flex-col gap-1">
          <span className="inline-flex flex-wrap items-center gap-2">
            {kind}
            <Badge variant={a.severity === "critical" ? "danger" : "warning"}>
              {a.severity === "critical" ? "Critical" : "Warning"}
            </Badge>
          </span>
          <span className="text-sm font-normal text-muted-foreground">{a.summary}</span>
        </span>
      </TableHead>
      <TableCell>
        {a.de_membership_id ? (
          <Link
            to="/c/$slug/agents/de/$id"
            params={{ slug: company.slug, id: a.de_membership_id }}
            search={keepFocus}
            className="text-primary-text underline-offset-4 hover:underline"
          >
            {names.get(a.de_membership_id) ?? "Digital employee"}
          </Link>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell>
        {a.run_id ? (
          <Link
            to="/c/$slug/agents/runs/$id"
            params={{ slug: company.slug, id: a.run_id }}
            search={keepFocus}
            className="font-mono text-xs text-primary-text underline-offset-4 hover:underline"
            data-slot="alert-run"
          >
            {a.run_id.slice(-8)}
          </Link>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell className="text-muted-foreground tabular-nums">
        {formatDateTime(a.opened_at)}
      </TableCell>
      <TableCell className="text-muted-foreground tabular-nums">
        {formatDateTime(a.last_seen_at)}
      </TableCell>
      <TableCell>
        <Badge variant={STATE_TONE[a.state]}>{ALERT_STATE_LABELS[a.state]}</Badge>
      </TableCell>
      <TableCell>
        {a.state === "resolved" ? null : (
          <span className="flex justify-end gap-1">
            {a.state === "open" ? (
              <Button
                size="sm"
                variant="outline"
                disabled={update.isPending}
                onClick={() => update.mutate("acked")}
                aria-label={`Ack: ${a.summary}`}
              >
                Ack
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              disabled={update.isPending}
              onClick={() => update.mutate("resolved")}
              aria-label={`Resolve: ${a.summary}`}
            >
              Resolve
            </Button>
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}
